import { Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../api/envelope';
import type { CampaignSettings, Suppression } from '../../api/types';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import { REASON_LABELS, releaseHint } from './campaignSettingsTools';
import { useComms } from './context';

/**
 * Opting out, both halves: what a campaign SAYS about leaving (the company's
 * footer wording), and who has already left (the do-not-contact list every
 * send screens against).
 *
 * The footer here is the company default — the words every campaign email
 * carries when it did not word its own (services/campaignFooter.js) — and
 * empty means the built-in sentence, shown as the placeholder so what is on
 * screen is what goes out. The list is the tenant's, across both channels,
 * and whether a row can come off is the server's answer by REASON: somebody's
 * own STOP or Unsubscribe is not an operator's to undo at any level.
 */
export function OptOutSection({ onOpenCampaign }: { onOpenCampaign: (id: number) => void }) {
  return (
    <>
      <FooterCard />
      <OptOutList onOpenCampaign={onOpenCampaign} />
    </>
  );
}

function FooterCard() {
  const { source, capabilities } = useComms();
  const mayChange = capabilities?.access.features.campaigns === 'full';
  const load = useLoad(() => source.api.getCampaignSettings(), [source]);
  const [saved, setSaved] = useState<CampaignSettings | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [linkText, setLinkText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const current = saved ?? load.data;

  if (!current) {
    return (
      <div className="cpset-card cpset-pad cpset-muted">
        {load.error ? 'Could not load the footer wording.' : 'Loading…'}
      </div>
    );
  }
  const footer = current.footer;
  // Untouched fields show what is saved; a field once typed in is the operator's.
  const shownText = text ?? footer.text ?? '';
  const shownLink = linkText ?? footer.linkText ?? '';
  const dirty = shownText.trim() !== (footer.text ?? '') || shownLink.trim() !== (footer.linkText ?? '');

  const save = async (next: { text: string; linkText: string }) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setSaved(await source.api.saveCampaignSettings({ footer: next }));
      setText(null);
      setLinkText(null);
      setNotice(next.text || next.linkText ? 'Saved. Campaigns that send from now on use it.' : 'Back to the built-in wording.');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The wording could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="cpset-card" aria-labelledby="cpset-footer-title">
      <div className="cpset-pad cpset-section-top">
        <h3 id="cpset-footer-title">The email footer</h3>
        <span className="cpset-hint">
          Every campaign email ends with a line saying why it was sent and a link to stop. This is the wording a
          campaign uses when it has not written its own.
        </span>
      </div>

      <div className="cpset-pad cpset-footer-grid">
        <div className="cpset-footer-fields">
          <label className="cmg-field">
            <span className="cmg-field-label">Sentence</span>
            <input
              className="cmg-input"
              value={shownText}
              maxLength={300}
              disabled={!mayChange}
              placeholder={footer.builtInText}
              onChange={(e) => {
                setText(e.target.value);
                setNotice(null);
              }}
            />
          </label>
          <label className="cmg-field">
            <span className="cmg-field-label">Link wording</span>
            <input
              className="cmg-input"
              value={shownLink}
              maxLength={60}
              disabled={!mayChange}
              placeholder={footer.builtInLinkText}
              onChange={(e) => {
                setLinkText(e.target.value);
                setNotice(null);
              }}
            />
          </label>
          <span className="cpset-hint">
            Empty uses the greyed-out wording. Data points work in the sentence — <code>{'{{company_name}}'}</code> —
            and one nothing fills is refused here, before it can reach a send. The link always works, whatever you
            call it.
          </span>
          {mayChange ? (
            <div className="cpset-inline">
              <button
                type="button"
                className="cbtn cbtn-primary"
                disabled={busy || !dirty}
                onClick={() => void save({ text: shownText.trim(), linkText: shownLink.trim() })}
              >
                Save
              </button>
              {(footer.text || footer.linkText) && (
                <button
                  type="button"
                  className="cbtn"
                  disabled={busy}
                  onClick={() => void save({ text: '', linkText: '' })}
                >
                  Use the built-in wording
                </button>
              )}
            </div>
          ) : (
            <span className="cpset-hint">Changing the footer wording needs Campaigns at Send.</span>
          )}
          {notice && <span className="cpset-hint cpset-ok-text">{notice}</span>}
          {error && <div className="cmg-insight bad">{error}</div>}
        </div>

        {/* What a recipient sees at the bottom of the mail — the service's own
            footer shape, with the words as they stand in the boxes. */}
        <div className="cpset-footer-preview" aria-label="How the footer looks">
          <span className="cpset-preview-label">How it looks</span>
          <div className="cpset-footer-paper">
            <span>{shownText.trim() || footer.builtInText}</span>
            <span className="cpset-footer-link">{shownLink.trim() || footer.builtInLinkText}</span>
          </div>
        </div>
      </div>

      <div className="cpset-pad cpset-sms-note">
        <b>Text messages</b>
        <span className="cpset-hint">
          Every campaign text ends with “{footer.smsFooter}” unless it already mentions STOP. Those words are fixed:
          carriers expect them, and STOP works whether a campaign shows them or not.
        </span>
      </div>
    </section>
  );
}

type ChannelFilter = 'all' | 'email' | 'sms';

function OptOutList({ onOpenCampaign }: { onOpenCampaign: (id: number) => void }) {
  const { source, capabilities, agents } = useComms();
  const { fmtDate } = useTimeFormat();
  const level = capabilities?.access.features.campaigns ?? 'full';
  const mayEdit = level === 'draft' || level === 'full';
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const list = useLoad(
    () => source.api.listSuppressions({ q: query || undefined, channel: channel === 'all' ? undefined : channel, page }),
    [source, query, channel, page],
  );
  const nameOf = (id: number | null) => (id ? (agents.find((agent) => agent.dashBoardId === id)?.name ?? null) : null);

  const remove = async (row: Suppression) => {
    setBusyId(row.id);
    setError(null);
    try {
      await source.api.removeSuppression(row.id);
      list.reload();
    } catch (e) {
      // The server's own sentence: it names who can undo it.
      setError(e instanceof ApiError ? e.message : 'That could not be taken off the list.');
    } finally {
      setBusyId(null);
    }
  };

  const data = list.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <section className="cpset-card" aria-labelledby="cpset-optout-title">
      <div className="cpset-pad cpset-section-top">
        <div className="cpset-title-row">
          <h3 id="cpset-optout-title">The opt-out list</h3>
          {data && <span className="cpset-count">{data.total.toLocaleString()}</span>}
          <div style={{ flex: 1 }} />
          {mayEdit && !adding && (
            <button type="button" className="cbtn" onClick={() => setAdding(true)}>
              <Plus size={13} />
              Add someone
            </button>
          )}
        </div>
        <span className="cpset-hint">
          Nobody here is sent a campaign, on either channel. Most of it writes itself — a press of Unsubscribe, a STOP
          reply, a carrier’s opt-out. Add somebody by hand when they ask you on the phone.
        </span>
      </div>

      {adding && (
        <AddRow
          onDone={() => {
            setAdding(false);
            setPage(1);
            list.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <div className="cpset-pad cpset-filter-row">
        <form
          className="cpset-search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setQuery(q.trim());
          }}
        >
          <Search size={14} aria-hidden />
          <input
            aria-label="Search the opt-out list"
            placeholder="Search an address, a number or a note"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              if (!e.target.value) {
                setQuery('');
                setPage(1);
              }
            }}
          />
        </form>
        <div className="chome-segments" role="group" aria-label="Channel">
          {(['all', 'email', 'sms'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`chome-seg${channel === value ? ' on' : ''}`}
              aria-pressed={channel === value}
              onClick={() => {
                setChannel(value);
                setPage(1);
              }}
            >
              {value === 'all' ? 'All' : value === 'email' ? 'Email' : 'Texts'}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="cpset-pad cpset-pad-tight">
          <div className="cmg-insight bad">{error}</div>
        </div>
      )}

      {!data ? (
        <div className="cpset-pad cpset-muted">{list.error ? 'Could not load the list.' : 'Loading…'}</div>
      ) : data.payload.length === 0 ? (
        <div className="cpset-pad cpset-muted">
          {query || channel !== 'all' ? 'Nobody on the list matches that.' : 'Nobody has opted out yet.'}
        </div>
      ) : (
        <div className="cpset-table-wrap cpset-table-flush">
          <table className="cpset-table">
            <thead>
              <tr>
                <th>Who</th>
                <th>Channel</th>
                <th>Why</th>
                <th>Added</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.payload.map((row) => {
                const by = row.reason === 'manual' ? nameOf(row.createdBy) : null;
                const hint = releaseHint(row);
                return (
                  <tr key={row.id}>
                    <td>
                      <span className="mono">{row.address}</span>
                      {row.note && <span className="cpset-row-note">{row.note}</span>}
                    </td>
                    <td>{row.channel === 'sms' ? 'Texts' : 'Email'}</td>
                    <td>
                      {REASON_LABELS[row.reason]}
                      {row.campaignId && (
                        <>
                          {' · '}
                          <button
                            type="button"
                            className="cmg-link-btn"
                            onClick={() => onOpenCampaign(row.campaignId as number)}
                          >
                            the campaign
                          </button>
                        </>
                      )}
                    </td>
                    <td className="cpset-nowrap">
                      {fmtDate(row.createdAt)}
                      {by && <span className="cpset-row-note">by {by}</span>}
                    </td>
                    <td className="cpset-cell-end">
                      {row.releasable && mayEdit ? (
                        <button
                          type="button"
                          className="cbtn cbtn-sm"
                          disabled={busyId === row.id}
                          onClick={() => void remove(row)}
                        >
                          Remove
                        </button>
                      ) : hint ? (
                        <span className="cpset-row-note">{hint}</span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && pages > 1 && (
        <div className="cpset-pad cpset-pager">
          <span className="cpset-hint">
            {(data.page - 1) * data.pageSize + 1}–{Math.min(data.page * data.pageSize, data.total)} of{' '}
            {data.total.toLocaleString()}
          </span>
          <button type="button" className="cbtn cbtn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <button
            type="button"
            className="cbtn cbtn-sm"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}

function AddRow({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { source } = useComms();
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await source.api.addSuppression({ channel, address: address.trim(), note: note.trim() || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That could not be added.');
      setBusy(false);
    }
  };

  return (
    <form
      className="cpset-pad cpset-add"
      onSubmit={(e) => {
        e.preventDefault();
        if (address.trim()) void add();
      }}
    >
      <label className="cmg-field cpset-add-channel">
        <span className="cmg-field-label">Channel</span>
        <select className="cmg-select" value={channel} onChange={(e) => setChannel(e.target.value as 'email' | 'sms')}>
          <option value="email">Email</option>
          <option value="sms">Texts</option>
        </select>
      </label>
      <label className="cmg-field">
        <span className="cmg-field-label">{channel === 'sms' ? 'Mobile number' : 'Email address'}</span>
        <input
          className="cmg-input"
          value={address}
          autoFocus
          placeholder={channel === 'sms' ? '+1 516 555 0100' : 'name@example.com'}
          onChange={(e) => setAddress(e.target.value)}
        />
      </label>
      <label className="cmg-field">
        <span className="cmg-field-label">Note (optional)</span>
        <input
          className="cmg-input"
          value={note}
          maxLength={255}
          placeholder="Asked on the phone"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="cpset-inline cpset-add-actions">
        <button type="submit" className="cbtn cbtn-primary" disabled={busy || !address.trim()}>
          Add to the list
        </button>
        <button type="button" className="cbtn" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {error && <div className="cmg-insight bad cpset-add-error">{error}</div>}
    </form>
  );
}
